import type { HexColor, ISO8601, UUID } from "./index.ts";
import type { InterviewMeta } from "./interview.ts";
import type { UserRole } from "./user.ts";

export type MeetingStage = "Intro" | "Discovery" | "Qualification" | "Negotiation";
export type MeetingType =
  | "sales"
  | "upsell"
  | "technical"
  | "training"
  | "onboarding"
  | "review"
  | "other"
  | "simulation"
  | "interview";
export type SimulationDifficulty = "beginner" | "intermediate" | "advanced";

/** Live hint "focus type" — how Sally shapes hints during the meeting.
 * - standard: default balanced coaching
 * - technical: deeper technical terminology / substance
 * - executive: concise, business-outcome framing
 * - deepdive: longer, more thorough hint answers
 * - pm (project manager): watch time/agenda; also flagged in summary + infographic */
export type HintFocus = "standard" | "technical" | "executive" | "deepdive" | "pm" | "training";

/** Human labels for the hint focus types (shared by the live selector). */
export const HINT_FOCUS_LABELS: Record<HintFocus, string> = {
  standard: "Standard",
  technical: "Technical",
  executive: "Executive",
  deepdive: "Deep dive",
  pm: "Project manager",
  training: "Training / learning",
};

export interface SimulationScenario {
  id: string;
  title: string;
  subtitle: string;
  persona: string;
  personaRole: string;
  company: string;
  industry: string;
  difficulty: SimulationDifficulty;
  gcpFocus: string[];
  hiddenObjections: string[];
  personalityTraits: string[];
  stage: MeetingStage;
  scenarioDescription: string;
  systemPromptOverlay: string;
}
export type SpeakerSide = "client" | "rep";
export type LangCode =
  | "en"
  | "he"
  | "fr"
  | "es"
  | "de"
  | "ar"
  | "pt"
  | "ja"
  | "ko"
  | "zh"
  | "it"
  | "nl"
  | "ru"
  | "tr"
  | "hi";
export type Sentiment = "buying" | "concern" | "positive" | "neutral";
export type HintCategory =
  | "Competitive"
  | "Problem→Solution"
  | "Commercial"
  | "Positive"
  | "Objection"
  | "Redirect"
  | "Knowledge base"
  // Meeting-prep reminder for a goal/topic not yet discussed (orange card).
  | "Reminder"
  // AIMS interview coaching categories (rendered by the same HintCard).
  | "Strong answer"
  | "Borderline answer"
  | "Weak answer"
  | "Interviewer tip";
export type HintColor = "blue" | "red" | "yellow" | "green" | "orange";
export type DealHealth = "hot" | "warm" | "cool" | "cold";

export interface Account {
  name: string;
  industry?: string;
  region?: string;
  contact?: string;
  contactRole?: string;
  deal?: string;
  arr?: string;
  website?: string;
  /** Slug of a researched CompanyProfile attached at setup time (full profile lives in `companyProfiles`). */
  companyProfileSlug?: string;
  /** True for an internal (no external customer) meeting — skips company research
   * and the company brief. Set from the quick-meeting internal/external step. */
  isInternal?: boolean;
}

export interface Participant {
  name: string;
  role: string;
  side: SpeakerSide;
  color: HexColor;
  initials: string;
}

export interface TranscriptLine {
  id: UUID;
  t: string;
  speaker: SpeakerSide;
  name: string;
  lang: LangCode;
  text: string;
  trans?: string;
  entities?: string[];
  sentiment?: Sentiment;
  isFinal: boolean;
}

export interface ComparisonSide {
  name: string;
  points: string[];
  verdict?: "strong" | "weak" | "neutral";
}

export interface ComparisonTable {
  /** e.g. "Bedrock vs Vertex AI Model Garden" */
  topic: string;
  left: ComparisonSide;
  right: ComparisonSide;
  recommendation?: string;
}

/** A citation to a knowledge-base document, attached to a RAG-grounded hint. */
export interface HintReference {
  docId: UUID;
  docName: string;
  /** 1-based page in the (canonical PDF) document. */
  page?: number;
  /** Link to open the document at the cited page, e.g. /documents/{id}/file#page=4 */
  url: string;
}

export interface Hint {
  id: UUID;
  title: string;
  /** Canonical topic key (set by the model) used to group same-subject hints into
   * one card in the UI — reliable across languages/categories. */
  topic?: string;
  category: HintCategory;
  color: HintColor;
  summary: string;
  proofPoints: string[];
  sources: string[];
  confidence: number;
  timestamp: string;
  actedOn?: boolean;
  /** When the hint compares two products (e.g. competitor vs GCP), the model
   * may include a structured table for side-by-side rendering. */
  comparisonTable?: ComparisonTable;
  /** "high" = triggered by a competitor mention or comparison question. UI
   * applies a red left rail to the card. */
  priority?: "normal" | "high";
  /** "kb" = a document-grounded hint from the knowledge base (RAG/File Search). */
  kind?: "kb";
  /** Document citations (set for kind="kb"). */
  references?: HintReference[];
  /** 2-4 keyword tags (set by the model) used for tag-chip rendering and
   * click-to-filter in the live coach column. */
  tags?: string[];
}

/** An admin-uploaded knowledge-base document (indexed in Gemini File Search). */
export interface DocumentRecord {
  id: UUID;
  /** Original file name / doc title shown to users. */
  name: string;
  /** Stored/served content type (the original file's type). */
  contentType: string;
  status: "processing" | "ready" | "failed";
  /** "upload" = file in GCS; "gdrive" = Google Doc/Drive link; "web" = public URL
   * fetched + extracted; "blog" = auto-ingested Google Cloud blog post; "usecase" =
   * auto-ingested cloud.google.com customer story. */
  source?: "upload" | "gdrive" | "web" | "blog" | "usecase";
  /** For source="gdrive"/"web"/"blog"/"usecase": the original URL (cited live). */
  sourceUrl?: string;
  /** For source="blog": the post's publication date (ISO) — used for the 90-day rolling window. */
  publishedAt?: ISO8601;
  /** GCS object path of the original file we serve for viewing (uploads only). */
  gcsPath?: string;
  /** File Search document resource name (fileSearchStores/.../documents/...). */
  fileSearchDoc?: string;
  uploadedBy: string;
  createdAt: ISO8601;
  error?: string;
}

export interface SentimentEvent {
  at: number;
  label: string;
  kind: Sentiment;
}

export interface SentimentSample {
  at: number;
  value: number;
  event?: SentimentEvent;
  /** Which side of the conversation the sentiment was measured on. Default
   * "all" for legacy combined samples. */
  speaker?: "rep" | "client" | "all";
}

export type ContextItemKind = "url" | "doc" | "case";

export interface ContextItem {
  kind: ContextItemKind;
  label: string;
  note?: string;
  href?: string;
}

export interface ContextFile {
  id: UUID;
  name: string;
  size: number;
  sha256: string;
  contentType: string;
  uploadedAt: ISO8601;
  /** True once extraction produced usable {@link text} (fed to live hints). */
  indexed: boolean;
  /** Extracted/summarized document text, capped. Concatenated into the live-hint
   * "Pre-call context" so hints can reference the doc's content. */
  text?: string;
  /** How the file got here. Default "upload". */
  source?: "upload" | "gdrive";
  /** For source="gdrive": the original Google Drive/Docs URL. */
  sourceUrl?: string;
  /** File Search document resource name — set when the doc was indexed into the
   * meeting's per-meeting store for Quiet-ask retrieval (used to delete it). */
  fileSearchDoc?: string;
  /** Set when text extraction failed/empty (file still listed, just not fed to hints). */
  extractError?: string;
}

export interface ContextInsight {
  entities: string[];
  painPoints: string[];
  tags: string[];
}

export interface RepNote {
  t: string;
  text: string;
  source?: "manual" | "auto";
}

export interface LiveTip {
  id: UUID;
  text: string;
  at: number;
}

export interface QuietAskEntry {
  q: string;
  a: string;
  chips: string[];
  at: ISO8601;
  urgent: boolean;
}

export interface Meeting {
  id: UUID;
  ownerUid: string;
  account: Account;
  title: string;
  goal?: string;
  /** Topics the rep wants to cover, parsed from the goal free-text. Drives the
   * live "open topics not yet discussed" orange reminder cards. */
  topics?: string[];
  /** The CE role chosen for THIS meeting (defaults to the owner's profile role).
   * Overrides the profile role for role-aware coaching. */
  role?: UserRole;
  /** Free-form organizational labels/tags for this meeting (e.g. "Migration",
   * "Q3 Renewal", "Security"). Suggested by the model from the meeting content and
   * editable by the user. Power the dashboard label filter/search and the
   * "ask across meetings by label" flow. */
  labels?: string[];
  stage: MeetingStage;
  meetingType?: MeetingType;
  /** Live "focus type" the rep picks in the hints panel — shapes hint tone/length
   * (and, for "pm", adds time-management notes to the summary + infographic).
   * Default "standard". */
  hintFocus?: HintFocus;
  /** Consent to share this meeting's product bug/feature insights with Google
   * product managers via the Voice of the Field report. Default true (opt-out);
   * when false, the meeting is excluded from VoF. */
  voiceOfFieldConsent?: boolean;
  /** The custom-question preset key selected for this meeting (a role or meeting
   * type). Resolves the live custom-questions set. Undefined = user default. */
  customQuestionKey?: string;
  parentMeetingId?: string;
  simulationScenarioId?: string;
  simulationScenario?: SimulationScenario;
  /** Interview-specific setup metadata (set when meetingType="interview"). */
  interview?: InterviewMeta;
  language: "auto" | LangCode;
  /** Language for Sally's ON-SCREEN assistance (hints, quick answers, quiet-ask,
   * live tips) — independent of the spoken meeting `language`. "auto" (or unset)
   * follows the spoken/meeting language; a specific code forces that language for
   * everything Sally shows the rep (e.g. meeting in English, hints in Hebrew). */
  assistanceLanguage?: "auto" | LangCode;
  /** The language the owner last chose to VIEW this meeting's summary/email in
   * (via the summary "view in language" switcher). Persisted so reopening the
   * summary auto-shows it in that language. "en"/unset = show the original. The
   * translation itself is cached on the summary doc under `translations[lang]`. */
  summaryLang?: "en" | LangCode;
  scheduledAt?: ISO8601;
  startedAt?: ISO8601;
  endedAt?: ISO8601;
  participants: Participant[];
  contextFiles: ContextFile[];
  /** Lazily-created per-meeting Gemini File Search store name. Holds this meeting's
   * context docs so Quiet-ask can retrieve/cite them WITHOUT leaking into other
   * meetings' or the global knowledge base. Deleted with the meeting. */
  contextStore?: string;
  contextItems: ContextItem[];
  notes: RepNote[];
  status: "draft" | "live" | "ended" | "summarized";
  /** True when the title is an auto-generated placeholder (e.g. a "quick meeting"
   * started without a name). The end-of-meeting flow offers a suggested name for
   * these; cleared (false) once the user approves/edits a real title. */
  autoTitled?: boolean;
  /** External meetings only: set true once the rep confirms (via the live
   * transcription-consent reminder) that they told participants the meeting is
   * transcribed. Undefined = never confirmed. Shown on the summary. */
  participantsNotified?: boolean;
  /** Audit: when {@link participantsNotified} was confirmed, and by whom (uid) —
   * an auditable consent-acknowledgement record for GDPR/CCPA. */
  participantsNotifiedAt?: ISO8601;
  participantsNotifiedBy?: string;
  createdAt: ISO8601;
  updatedAt: ISO8601;
}

export interface MeetingDraft {
  account: Pick<Account, "name" | "website" | "companyProfileSlug" | "isInternal">;
  title: string;
  stage: MeetingStage;
  /** Mark a quick/unnamed meeting so we suggest a name when it ends. */
  autoTitled?: boolean;
}

export interface MeetingSetup extends MeetingDraft {
  goal?: string;
  language: "auto" | LangCode;
  /** Language for Sally's on-screen hints/answers; "auto"/unset follows `language`. */
  assistanceLanguage?: "auto" | LangCode;
  hintTone?: "direct" | "consultative" | "brief";
}

export interface HistoryItem {
  id: UUID;
  client: string;
  /** Client company website/domain (from the meeting account), for showing a logo. */
  website?: string;
  title: string;
  date: ISO8601;
  duration: string;
  rep: string;
  stage: MeetingStage;
  /** Live state — drives whether the row links to /live (resume) or /summary. */
  status?: "draft" | "live" | "ended" | "summarized";
  /** Set once the meeting has been ended at least once. A resumed meeting shows status
   * "live" again but keeps endedAt — so the dashboard routes it to the summary instead
   * of trapping it on the live screen. */
  endedAt?: ISO8601;
  meetingType?: MeetingType;
  scheduledAt?: ISO8601;
  score: number;
  sentiment: Sentiment;
  tags: string[];
  hintCount: number;
  actedOn: number;
  actionItemCount?: number;
  actionItemDone?: number;
  nextStep?: string;
  avatar: HexColor;
  participants?: string[];
  sharedBy?: { uid: string; name: string; initials: string; color: HexColor; role?: string };
  sharedAt?: ISO8601;
}

export interface UpsellOpportunity {
  name: string;
  reason: string;
  estimatedMonthlyArr?: number;
}

export interface ActionItem {
  id: UUID;
  who: string;
  what: string;
  due: ISO8601;
  done: boolean;
}

export interface ReferenceLink {
  title: string;
  href: string;
  source: string;
}

/** A founder / co-founder of a researched company. */
export interface CompanyFounder {
  name: string;
  /** e.g. "Co-founder & CEO". */
  role?: string;
  /** 1-2 sentence background. */
  background?: string;
  /** LinkedIn profile URL (typically from Apollo People Search). */
  linkedin?: string;
  /** Best-effort headshot URL (from Apollo People Search); UI falls back to initials. */
  photoUrl?: string;
  /** A recent notable thing they said/did/were mentioned for (research, best-effort). */
  recentMention?: string;
}

/** A recent news item / press mention about a company or its founders. */
export interface CompanyNews {
  title: string;
  url?: string;
  source?: string;
  /** ISO or human date, e.g. "2026-07". */
  date?: string;
}

/** A competitor with enough detail to show a logo + link. */
export interface CompetitorRef {
  name: string;
  /** Registrable domain, e.g. "acme.com" (used for the Clearbit logo). */
  domain?: string;
  /** Logo URL (typically https://logo.clearbit.com/<domain>); UI hides on error. */
  logoUrl?: string;
  /** One line: who they are / how they compete. */
  description?: string;
}

/** A single funding round. Amounts are strings to preserve currency formatting. */
export interface CompanyFundingRound {
  /** e.g. "Series B". */
  round?: string;
  /** e.g. "$45M". */
  amount?: string;
  /** ISO date or human "2023-09". */
  date?: string;
  leadInvestors?: string[];
}

export interface CompanyFunding {
  /** e.g. "$120M total". */
  totalRaised?: string;
  rounds: CompanyFundingRound[];
  investors: string[];
}

/**
 * A web-researched profile of a company, cached in the shared `companyProfiles`
 * collection (keyed by `slug`) so research is done once and reused across reps.
 * Produced by Gemini + Google Search grounding.
 */
export interface CompanyProfile {
  /** Normalized slug used as the Firestore doc id + cache key. */
  slug: string;
  /** Display name as researched (may be normalized from the rep's input). */
  name: string;
  website?: string;
  /** Company LinkedIn URL (typically from Apollo). */
  linkedinUrl?: string;
  /** Short factual description of what the company does. */
  overview: string;
  industry?: string;
  /** HQ / primary location. */
  hq?: string;
  yearEstablished?: string;
  /** Approx. headcount, e.g. "201-500" or "350" (from Apollo). */
  employees?: string;
  /** Estimated annual revenue, formatted, e.g. "$10M" (from Apollo). */
  annualRevenue?: string;
  /** Number of open positions found on the company's careers/jobs page (a hiring
   * signal). Undefined when not found. */
  openPositions?: number;
  /** Link to the company's careers/jobs page backing `openPositions`. */
  openPositionsUrl?: string;
  founders: CompanyFounder[];
  funding?: CompanyFunding;
  techStack: string[];
  competitors: string[];
  /** Likely GCP CE pain points / engagement context. */
  engagementContext?: string;
  cloudChallenges: string[];
  sources: ReferenceLink[];
  /** Markdown rendering of the 4-section profile (for review display + export). */
  markdown: string;
  /** false when the company could not be confidently identified. */
  found: boolean;
  researchedAt: ISO8601;
  researchedByUid?: string;
  /** Set when a user has hand-corrected the profile; guards against auto-enrichment
   * (e.g. Apollo backfill) overwriting their edits. */
  edited?: boolean;

  // --- Deep-research ("Discovery") enrichment (all optional, best-effort) ---
  /** Company logo URL (typically https://logo.clearbit.com/<domain>); UI hides on error. */
  logoUrl?: string;
  /** Recent news / press mentions about the company or its founders. */
  news?: CompanyNews[];
  /** Competitors with domain + logo (richer than the `competitors` name list). */
  competitorDetails?: CompetitorRef[];
  /** Estimated market size / TAM, e.g. "~$40B global market". */
  marketSize?: string;
  /** Notable market/industry trends relevant to the company. */
  marketTrends?: string[];
  /** Where Google Cloud should focus for this account (FSR tips / opportunities). */
  gcpOpportunities?: string[];
}

/**
 * A saved "Discovery" — a per-user deep-research workspace for a company. The
 * researched profile itself lives in the shared `companyProfiles` cache (by
 * `slug`); this record holds the user's own free text, notes, uploaded docs, and
 * a link to a meeting started from it. Pinned at the top of the dashboard.
 */
export interface Discovery {
  id: string;
  /** Company slug — the key into the shared `companyProfiles` cache. */
  slug: string;
  companyName: string;
  website?: string;
  ownerUid: string;
  createdAt: ISO8601;
  updatedAt: ISO8601;
  /** Free text the user typed about the company. */
  freeText?: string;
  /** People to research (names, e.g. from a calendar invite when the discovery was
   * started from a meeting). */
  people?: string[];
  /** Gemini/web-grounded notes about {@link people} (short profile per person). */
  peopleNotes?: string;
  /** The user's personal notes. */
  notes?: string;
  /** Docs/context the user attached to prep for the meeting. */
  attachments: ContextFile[];
  /** Set once a meeting has been started from this discovery. */
  meetingId?: string;
}

/**
 * Lead-in overview shown at the very top of the summary (before "What went well").
 * A plain-language recap of the meeting plus a Gemini-grounded brief on the
 * company the meeting was with (background + investment/funding status).
 */
export interface MeetingOverview {
  /** Clear, plain-language narrative summary of the whole meeting. */
  summary: string;
  /** Concrete things that were done / decided / accomplished in the meeting. */
  whatWasDone: string[];
  /** The company the meeting was with (best-effort from the transcript). */
  companyName: string;
  /** People who joined the meeting, by name (with role where known). */
  attendees: string[];
  /** Web-grounded background brief on the company (via Gemini + Google Search). */
  companyBrief?: string;
  /** Funding/investment status from web search (e.g. "Raised $12M Series A …" or "No public funding found"). */
  investmentStatus?: string;
  /** Source links backing the company brief. */
  companySources?: ReferenceLink[];
  /** Set when the user manually edits the overview (summary text / attendees) on
   * the summary page. Informational guard, mirrors ClientEmail.edited. */
  edited?: boolean;
}

/**
 * One answer in the per-user "Custom summary" block. The user defines the
 * questions (see UserSettings.customQuestions); the model answers each, preferring
 * what was said in the meeting and falling back to a web search about the company.
 */
export interface CustomAnswer {
  question: string;
  answer: string;
  /** Where the answer came from: "meeting" = stated in the call, "web" = searched
   * online, "apollo" = found in Apollo.io, "not-found" = no answer available. */
  source: "meeting" | "web" | "apollo" | "not-found";
  /** Links backing a web-sourced answer. */
  sources?: ReferenceLink[];
  /** Set when the user hand-edits the answer on the summary page. */
  edited?: boolean;
}

export interface InternalSummary {
  confidence: number;
  health: DealHealth;
  score: number;
  /** Lead-in overview rendered before "What went well". */
  overview?: MeetingOverview;
  wentWell: string[];
  couldImprove: string[];
  upsell: UpsellOpportunity[];
  risks: string[];
  needs: { stated: string[]; actual: string[] };
  actionItems: ActionItem[];
  topMoments: { t: string; type: string; quote: string }[];
  hintsSurfaced?: number;
  hintsActed?: number;
  sentimentDelta?: number;
  sentimentAvg?: number;
  /** A Mermaid diagram of the architecture/solution discussed, when one came up.
   * Rendered on the summary page. Absent when no architecture was discussed. */
  architectureDiagram?: { title: string; mermaid: string };
  /** All diagrams generated live during the meeting (architecture + flowcharts),
   * surfaced on the summary page. Empty/absent when none were drawn. */
  diagrams?: LiveDiagram[];
  /** True when summary generation ran but produced NO diagram — i.e. the model
   * judged there was nothing structural to visualize (a discovery/QBR/1:1/strategy
   * call), rather than a failure. Lets the summary UI distinguish "nothing to draw"
   * (offer a "Generate anyway" action) from a diagram that simply hasn't run yet.
   * Absent on older summaries and whenever a diagram WAS produced. */
  diagramSkipped?: boolean;
  questionsAsked?: QuietAskEntry[];
  actedHints?: Hint[];
  /** Per-user custom Q&A block (answers to UserSettings.customQuestions). */
  customSummary?: CustomAnswer[];
}

export interface ClientEmail {
  subject: string;
  greeting: string;
  body: string[];
  signoff: string;
  tone: "formal" | "warm" | "brief";
  references?: ReferenceLink[];
  /** When set, takes precedence over greeting+body+signoff for display/send.
   * Set by the user via the editable textarea + Save. Unset = render structured. */
  bodyText?: string;
  /** Set when the user has manually edited the email — guards against accidental
   * regenerate clobbering. */
  edited?: boolean;
  /** ISO timestamp of last manual edit. */
  editedAt?: ISO8601;
}

/** A saved snapshot of a MeetingSummary, kept so the user can re-run the summary
 * and still restore a previous version. `summary` is omitted in list responses. */
export interface SummaryVersion {
  id: string;
  /** ISO timestamp when this snapshot was taken. */
  savedAt: ISO8601;
  /** Short human label, e.g. "Before re-run" or "v3". */
  label: string;
  /** The full summary at that point (present when fetching a single version). */
  summary?: MeetingSummary;
}

export interface MeetingSummary {
  meetingId: UUID;
  meeting: {
    client: string;
    title: string;
    date: ISO8601;
    duration: string;
    /** When the meeting ended (ISO). Shown as the finish time on the summary. */
    endedAt?: ISO8601;
    participants: string[];
    meetingType?: MeetingType;
    /** Resolved meeting language (omitted when auto). Drives read-aloud voice
     * and dictation language on the summary screen. */
    language?: LangCode;
    /** The custom-question preset key chosen for this meeting (role / meeting type), so
     * the summary resolves the same question set that was used live. */
    customQuestionKey?: string;
  };
  internal: InternalSummary;
  client: ClientEmail;
  references: ReferenceLink[];
  generatedAt: ISO8601;
  generationLatencyMs: number;
  /** Cached on-demand translations of the summary + client email, keyed by LangCode.
   * Populated by POST /meetings/:id/translate so "view in the meeting language" is instant. */
  translations?: Record<string, SummaryTranslation>;
}

/** A translated overlay of the summary's + email's key textual fields (see
 * POST /meetings/:id/translate). Only fields present are overlaid on the English original. */
export interface SummaryTranslation {
  lang: LangCode;
  overviewSummary?: string;
  whatWasDone?: string[];
  wentWell?: string[];
  couldImprove?: string[];
  risks?: string[];
  needsStated?: string[];
  needsActual?: string[];
  actionItems?: string[];
  topMoments?: string[];
  questionsAsked?: string[];
  customAnswers?: string[];
  upsell?: { name: string; reason: string }[];
  email?: {
    subject?: string;
    greeting?: string;
    body?: string[];
    signoff?: string;
  };
}

export type InfographicKind = "flow" | "timeline" | "comparison" | "steps" | "gantt";

export interface FlowNode {
  id: string;
  label: string;
}
export interface FlowEdge {
  from: string;
  to: string;
  label?: string;
}
export interface FlowData {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface TimelineEntry {
  label: string;
  date?: string;
  detail?: string;
}
export interface TimelineData {
  entries: TimelineEntry[];
}

export interface ComparisonCol {
  header: string;
  items: string[];
}
export interface ComparisonData {
  columns: ComparisonCol[];
}

export interface StepData {
  steps: { title: string; detail?: string }[];
}

export interface GanttTask {
  name: string;
  start: string;
  end: string;
}
export interface GanttData {
  tasks: GanttTask[];
}

export interface Infographic {
  id: string;
  kind: InfographicKind;
  title: string;
  data: FlowData | TimelineData | ComparisonData | StepData | GanttData;
  mermaid?: string;
  generatedAt: string;
}

export interface InfographicImage {
  id: string;
  imageBase64?: string;
  imageUrl?: string;
  mimeType: string;
  prompt: string;
  generatedAt: string;
}

export interface Screenshot {
  id: string;
  imageUrl?: string;
  imageBase64?: string;
  mimeType: string;
  capturedAt: string;
  analysisHints?: Hint[];
}

/**
 * A recently-released Google product/feature/model relevant to the live
 * conversation, surfaced in the "What's New" panel. Grounded via Google Search
 * so it reflects current releases; only shown when released within ~3 months.
 */
export interface Announcement {
  id: string;
  title: string;
  productName: string;
  /** 1-2 sentences: what it is + why it matters to this conversation. */
  summary: string;
  /** Source link from grounding. */
  url?: string;
  /** Release date as found via search (ISO or human-readable). */
  releaseDate: string;
  /** Relative recency, e.g. "Just released", "Last week", "1 month ago". */
  recencyLabel: string;
  /** What in the conversation this ties to. */
  relatedTopic: string;
  /** 2-4 keyword tags for tag-chip rendering / filtering. */
  tags?: string[];
  generatedAt: string;
}

/**
 * A Google Cloud customer story (from cloud.google.com/customers) surfaced live
 * when it's relevant to the conversation. Ingested into File Search by the
 * usecases ingest job and matched against the rolling transcript, mirroring the
 * "What's New" announcement flow. Rendered in the live "Use Cases" tab.
 */
export interface UseCase {
  id: string;
  title: string;
  /** The customer/company the story is about. */
  customer: string;
  /** Industry/vertical, when known (e.g. "Retail", "Healthcare"). */
  industry?: string;
  /** 1-2 sentences: what they did + the outcome, tied to this conversation. */
  summary: string;
  /** Google Cloud products featured in the story. */
  products: string[];
  /** How suitable/recommended this case is for the current conversation:
   * 1 = loosely related, 2 = relevant, 3 = highly relevant. */
  rating: 1 | 2 | 3;
  /** What in the conversation this ties to. */
  relatedTopic: string;
  /** Source link (cloud.google.com/customers/...). */
  url: string;
  /** 2-4 keyword tags for tag-chip rendering / filtering. */
  tags?: string[];
  generatedAt: string;
}

/**
 * A live architecture/technical diagram generated during a technical meeting.
 * Multiple may be produced across the call (labelled #1, #2, …); rendered in the
 * live "Diagram" tab and flagged with a red "new" indicator until viewed.
 */
export interface LiveDiagram {
  id: string;
  /** Short human title of what the diagram depicts. */
  title: string;
  /** Mermaid source (flowchart …). */
  mermaid: string;
  /** Sequence label within this meeting (1-based). */
  index: number;
  generatedAt: string;
  /** Clarification answers the user supplied before this diagram was drawn (if any). */
  answers?: { question: string; answer: string }[];
  /** True when the user explicitly requested this diagram (vs. the auto ~7-min pass). */
  generatedOnDemand?: boolean;
  /** What kind of diagram this is — architecture flow vs a decision-tree/flowchart. */
  kind?: "architecture" | "flowchart";
}

/** One predefined answer for a diagram clarification question. Exactly one option in a
 * question should be marked `recommended` — the client pre-selects it. */
export interface ClarificationOption {
  value: string;
  label: string;
  /** The single most likely answer given the call so far; pre-selected in the UI. */
  recommended?: boolean;
  /** Optional short extra context shown under the option. */
  description?: string;
}

/** A single clarification question asked before generating an architecture diagram. */
export interface DiagramClarificationQuestion {
  id: string;
  text: string;
  /** Optional hint explaining why the question matters. */
  hint?: string;
  /** 2–5 predefined answers rendered as radio options. */
  options: ClarificationOption[];
}

/** Result of a diagram readiness check: either ready to draw, or a set of questions
 * to ask the user first. Persisted at meetings/{id}/diagramClarifications/current. */
export interface DiagramClarifyResult {
  ready: boolean;
  questions?: DiagramClarificationQuestion[];
  /** Server timestamp of when this check ran (for freshness / dedupe). */
  generatedAt?: string;
}

/** One section of the company report (research / billing / growth). Each carries its
 * own status so one can fail while the others succeed. `markdown` (research/growth) or
 * `report` (billing) hold the renderable text; `charts` is optional billing chart data;
 * `code` is a section-level error code (e.g. "no_billing_data", "session_expired"). */
/** A single chart in the billing section, in Chart.js-style config (what the CRM
 * emits). Kept permissive; the client renders known shapes and falls back to the
 * markdown table otherwise. */
export interface BillingChart {
  title?: string;
  type?: "bar" | "line" | "pie" | "doughnut" | string;
  data?: {
    labels?: (string | number)[];
    datasets?: { label?: string; data: number[] }[];
  };
}

export interface CompanyReportSection {
  status: "ok" | "error" | string;
  markdown?: string;
  report?: string;
  /** Billing chart data (array of Chart.js-style configs, or a record of them). */
  charts?: BillingChart[] | Record<string, BillingChart> | unknown;
  code?: string;
}

/** The meeting-start company report shown in the Research / Billing / Growth tabs.
 * Research is generated by Sally's own Gemini research; billing + growth come from the
 * CRM `/api/g/reports` endpoint (run as the end user via their BigQuery token). */
export interface CompanyReport {
  company: { requested: string; resolved?: string; fallback?: boolean };
  research: CompanyReportSection;
  billing: CompanyReportSection;
  growth: CompanyReportSection;
}

export type SharePermission = "view" | "comment" | "edit";

export interface Share {
  id: UUID;
  meetingId: UUID;
  recipientUid?: string;
  recipientEmail: string;
  permission: SharePermission;
  external: boolean;
  message?: string;
  createdAt: ISO8601;
  createdByUid: string;
}
